import { socketService } from '@/plugins/socket';

export default {
    namespaced: true,

    state() {
        return {
            socket: null,
            connected: false,
            listenersRegistered: false, // Track if socket listeners are registered
            conversations: [],
            activeConversation: null,
            messages: {}, // { conversationId: [messages] }
            unreadCounts: {}, // { conversationId: count }
            totalUnread: 0,
            typingUsers: {}, // { conversationId: { userId: boolean } }
            onlineUsers: {}, // { oduserId: boolean }
            widgetOpen: false,
            widgetMinimized: false,
            loading: false,
            loadingMessages: false,
            error: null,
            hasMoreMessages: {} // { conversationId: boolean }
        };
    },

    mutations: {
        SET_SOCKET(state, socket) {
            state.socket = socket;
        },

        SET_CONNECTED(state, connected) {
            state.connected = connected;
        },

        SET_LISTENERS_REGISTERED(state, registered) {
            state.listenersRegistered = registered;
        },

        SET_CONVERSATIONS(state, conversations) {
            state.conversations = conversations;
        },

        ADD_CONVERSATION(state, conversation) {
            const index = state.conversations.findIndex(c => c._id === conversation._id);
            if (index === -1) {
                state.conversations.unshift(conversation);
            } else {
                state.conversations.splice(index, 1, conversation);
            }
        },

        UPDATE_CONVERSATION(state, { conversationId, updates }) {
            const conversation = state.conversations.find(c => c._id === conversationId);
            if (conversation) {
                Object.assign(conversation, updates);
            }
            // Re-sort by updatedAt
            state.conversations.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        },

        SET_ACTIVE_CONVERSATION(state, conversation) {
            state.activeConversation = conversation;
        },

        SET_MESSAGES(state, { conversationId, messages, append = false }) {
            if (append && state.messages[conversationId]) {
                // Prepend older messages
                state.messages[conversationId] = [...messages, ...state.messages[conversationId]];
            } else {
                state.messages = {
                    ...state.messages,
                    [conversationId]: messages
                };
            }
        },

        ADD_MESSAGE(state, { conversationId, message }) {
            if (!state.messages[conversationId]) {
                state.messages[conversationId] = [];
            }

            // Check if message already exists (by _id or tempId)
            const existingIndex = state.messages[conversationId].findIndex(
                m => m._id === message._id || (message.tempId && m.tempId === message.tempId)
            );

            if (existingIndex !== -1) {
                // Update existing message
                state.messages[conversationId].splice(existingIndex, 1, message);
            } else {
                // Add new message
                state.messages[conversationId].push(message);
            }
        },

        UPDATE_MESSAGE(state, { conversationId, messageId, updates }) {
            if (state.messages[conversationId]) {
                const message = state.messages[conversationId].find(m => m._id === messageId);
                if (message) {
                    Object.assign(message, updates);
                }
            }
        },

        SET_UNREAD_COUNT(state, { conversationId, count }) {
            state.unreadCounts = {
                ...state.unreadCounts,
                [conversationId]: count
            };
            // Recalculate total
            state.totalUnread = Object.values(state.unreadCounts).reduce((sum, c) => sum + c, 0);
        },

        INCREMENT_UNREAD(state, { conversationId }) {
            const current = state.unreadCounts[conversationId] || 0;
            state.unreadCounts = {
                ...state.unreadCounts,
                [conversationId]: current + 1
            };
            state.totalUnread = Object.values(state.unreadCounts).reduce((sum, c) => sum + c, 0);
        },

        CLEAR_UNREAD(state, conversationId) {
            const previousCount = state.unreadCounts[conversationId] || 0;
            state.unreadCounts = {
                ...state.unreadCounts,
                [conversationId]: 0
            };
            state.totalUnread = Math.max(0, state.totalUnread - previousCount);
        },

        SET_TYPING(state, { conversationId, userId, isTyping }) {
            if (!state.typingUsers[conversationId]) {
                state.typingUsers = {
                    ...state.typingUsers,
                    [conversationId]: {}
                };
            }
            state.typingUsers[conversationId] = {
                ...state.typingUsers[conversationId],
                [userId]: isTyping
            };
        },

        SET_ONLINE(state, { userId, isOnline }) {
            state.onlineUsers = {
                ...state.onlineUsers,
                [userId]: isOnline
            };
        },

        TOGGLE_WIDGET(state) {
            state.widgetOpen = !state.widgetOpen;
            if (state.widgetOpen) {
                state.widgetMinimized = false;
            }
        },

        SET_WIDGET_OPEN(state, isOpen) {
            state.widgetOpen = isOpen;
            if (isOpen) {
                state.widgetMinimized = false;
            }
        },

        SET_WIDGET_MINIMIZED(state, isMinimized) {
            state.widgetMinimized = isMinimized;
        },

        SET_LOADING(state, loading) {
            state.loading = loading;
        },

        SET_LOADING_MESSAGES(state, loading) {
            state.loadingMessages = loading;
        },

        SET_ERROR(state, error) {
            state.error = error;
        },

        SET_HAS_MORE_MESSAGES(state, { conversationId, hasMore }) {
            state.hasMoreMessages = {
                ...state.hasMoreMessages,
                [conversationId]: hasMore
            };
        },

        CLEAR_STATE(state) {
            state.conversations = [];
            state.activeConversation = null;
            state.messages = {};
            state.unreadCounts = {};
            state.totalUnread = 0;
            state.typingUsers = {};
            state.onlineUsers = {};
            state.widgetOpen = false;
            state.loading = false;
            state.error = null;
            state.listenersRegistered = false;
        }
    },

    actions: {
        // Initialize socket connection
        initSocket({ commit, dispatch, state }) {
            console.log('[Chat Store] initSocket called, listenersRegistered:', state.listenersRegistered);
            const store = this;
            const socket = socketService.connect(store);

            if (socket && !state.listenersRegistered) {
                // Register socket event listeners only once
                console.log('[Chat Store] Registering listeners for first time');
                commit('SET_LISTENERS_REGISTERED', true);
                dispatch('registerSocketListeners');
            } else if (socket) {
                console.log('[Chat Store] Socket exists, listeners already registered');
            } else {
                console.log('[Chat Store] No socket returned from connect');
            }
        },

        // Disconnect socket
        disconnectSocket({ commit }) {
            socketService.disconnect();
            commit('SET_CONNECTED', false);
            commit('SET_SOCKET', null);
            commit('SET_LISTENERS_REGISTERED', false);
        },

        // Register socket event listeners
        registerSocketListeners({ commit, state, dispatch, rootState }) {
            console.log('[Chat Store] Registering socket event listeners');
            // Store reference to rootState for use in callbacks
            const storeRootState = rootState;
            const chatState = state;

            // New message received
            socketService.on('new_message', (data) => {
                const { message, conversationId, tempId } = data;
                console.log('[Chat Store] new_message received:', { conversationId, messageId: message._id, tempId });

                // Check if this message is from current user (already handled by optimistic update)
                const currentUserId = storeRootState && storeRootState.user && storeRootState.user._id;
                const senderId = message.sender && (message.sender._id || message.sender);
                console.log('[Chat Store] currentUserId:', currentUserId, 'senderId:', senderId);

                const isOwnMessage = senderId && currentUserId && senderId.toString() === currentUserId.toString();

                // Update the message in the store
                commit('ADD_MESSAGE', { conversationId, message: { ...message, tempId } });

                // Always update conversation's last message (for both own and received messages)
                commit('UPDATE_CONVERSATION', {
                    conversationId,
                    updates: {
                        lastMessage: {
                            content: message.content,
                            sender: message.sender,
                            sentAt: message.createdAt,
                            type: message.type
                        },
                        updatedAt: message.createdAt
                    }
                });

                // If not our own message and not the active conversation, increment unread
                if (!isOwnMessage && (!chatState.activeConversation || chatState.activeConversation._id !== conversationId)) {
                    commit('INCREMENT_UNREAD', { conversationId });
                }
            });

            // Unread count updated
            socketService.on('unread_count_updated', (data) => {
                const { conversationId, increment } = data;
                if (increment) {
                    commit('INCREMENT_UNREAD', { conversationId });
                }
            });

            // Conversation updated
            socketService.on('conversation_updated', (data) => {
                const { conversationId, lastMessage, unread } = data;
                commit('UPDATE_CONVERSATION', {
                    conversationId,
                    updates: { lastMessage }
                });
                if (unread !== undefined) {
                    commit('SET_UNREAD_COUNT', { conversationId, count: unread });
                }
            });

            // Messages read
            socketService.on('messages_read', (data) => {
                const { conversationId, readAt } = data;
                if (state.messages[conversationId]) {
                    state.messages[conversationId].forEach(msg => {
                        if (!msg.readAt) {
                            commit('UPDATE_MESSAGE', {
                                conversationId,
                                messageId: msg._id,
                                updates: { readAt, status: 'read' }
                            });
                        }
                    });
                }
            });

            // User typing
            socketService.on('user_typing', (data) => {
                const { conversationId, userId, isTyping } = data;
                commit('SET_TYPING', { conversationId, userId, isTyping });

                // Auto-clear typing after 3 seconds
                if (isTyping) {
                    setTimeout(() => {
                        commit('SET_TYPING', { conversationId, userId, isTyping: false });
                    }, 3000);
                }
            });

            // User online/offline
            socketService.on('user_online', (data) => {
                const { userId, isOnline } = data;
                commit('SET_ONLINE', { userId, isOnline });
            });

            // Online status response
            socketService.on('online_status_response', (data) => {
                const { statuses } = data;
                Object.entries(statuses).forEach(([userId, isOnline]) => {
                    commit('SET_ONLINE', { userId, isOnline });
                });
            });

            // Alert triggered
            socketService.on('alert_triggered', (data) => {
                // Emit a Vue event or show notification
                console.log('Alert triggered:', data);
            });

            // User joined conversation
            socketService.on('user_joined', (data) => {
                console.log('User joined:', data);
            });

            // User left conversation
            socketService.on('user_left', (data) => {
                console.log('User left:', data);
            });
        },

        // Fetch conversations
        async fetchConversations({ commit }, server) {
            commit('SET_LOADING', true);
            try {
                const response = await server.apiCall('conversations', 'getMyConversations', {}, 'get');
                if (response.content && response.content.results) {
                    commit('SET_CONVERSATIONS', response.content.results);

                    // Calculate unread counts
                    response.content.results.forEach(conv => {
                        const unread = conv.unreadBySeller || conv.unreadByAM || 0;
                        commit('SET_UNREAD_COUNT', { conversationId: conv._id, count: unread });
                    });
                }
            } catch (error) {
                commit('SET_ERROR', error.message);
                console.error('Failed to fetch conversations:', error);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        // Fetch or create conversation
        async getOrCreateConversation({ commit }, { server, sellerId }) {
            commit('SET_LOADING', true);
            try {
                const response = await server.apiCall('conversations', 'getOrCreateConversation', { sellerId }, 'post');
                if (response.content) {
                    commit('ADD_CONVERSATION', response.content);
                    return response.content;
                }
            } catch (error) {
                commit('SET_ERROR', error.message);
                console.error('Failed to get/create conversation:', error);
            } finally {
                commit('SET_LOADING', false);
            }
            return null;
        },

        // Fetch messages for a conversation
        async fetchMessages({ commit, state }, { server, conversationId, page = 1, limit = 50 }) {
            commit('SET_LOADING_MESSAGES', true);
            try {
                const response = await server.apiCall('messages', 'getMessages', {
                    conversationId,
                    page,
                    limit
                }, 'get');

                if (response.content && response.content.results) {
                    const append = page > 1;
                    commit('SET_MESSAGES', {
                        conversationId,
                        messages: response.content.results,
                        append
                    });

                    // Check if there are more messages
                    const hasMore = response.content.results.length >= limit;
                    commit('SET_HAS_MORE_MESSAGES', { conversationId, hasMore });
                }
            } catch (error) {
                commit('SET_ERROR', error.message);
                console.error('Failed to fetch messages:', error);
            } finally {
                commit('SET_LOADING_MESSAGES', false);
            }
        },

        // Send message via REST API (fallback)
        async sendMessageRest({ commit }, { server, conversationId, content, type, attachment }) {
            try {
                const response = await server.apiCall('messages', 'sendMessage', {
                    conversationId,
                    content,
                    type,
                    attachment
                }, 'post');

                if (response.content) {
                    commit('ADD_MESSAGE', { conversationId, message: response.content });
                    return response.content;
                }
            } catch (error) {
                commit('SET_ERROR', error.message);
                console.error('Failed to send message:', error);
            }
            return null;
        },

        // Set active conversation
        setActiveConversation({ commit, state }, conversation) {
            // Leave previous conversation room
            if (state.activeConversation) {
                socketService.leaveConversation(state.activeConversation._id);
            }

            commit('SET_ACTIVE_CONVERSATION', conversation);

            if (conversation) {
                // Join new conversation room
                socketService.joinConversation(conversation._id);

                // Mark as read
                socketService.markAsRead(conversation._id);
                commit('CLEAR_UNREAD', conversation._id);
            }
        },

        // Send message via socket
        sendMessage({ commit, state }, { conversationId, content, type = 'text', attachment = null }) {
            const tempId = `temp_${Date.now()}`;

            // Optimistic update
            const tempMessage = {
                _id: tempId,
                tempId,
                conversation: conversationId,
                content,
                type,
                attachment,
                sender: { _id: 'current' }, // Will be replaced by actual message
                status: 'sending',
                createdAt: new Date().toISOString()
            };

            commit('ADD_MESSAGE', { conversationId, message: tempMessage });

            // Send via socket
            socketService.sendMessage(conversationId, content, type, attachment, tempId);
        },

        // Mark conversation as read
        async markAsRead({ commit }, { server, conversationId }) {
            try {
                await server.apiCall('conversations', 'markAsRead', { _id: conversationId }, 'put');
                commit('CLEAR_UNREAD', conversationId);
            } catch (error) {
                console.error('Failed to mark as read:', error);
            }
        },

        // Start typing indicator
        startTyping({ state }, conversationId) {
            socketService.startTyping(conversationId);
        },

        // Stop typing indicator
        stopTyping({ state }, conversationId) {
            socketService.stopTyping(conversationId);
        },

        // Get online status for users
        getOnlineStatus({ commit }, userIds) {
            socketService.getOnlineStatus(userIds);
        },

        // Toggle widget
        toggleWidget({ commit }) {
            commit('TOGGLE_WIDGET');
        },

        // Open widget
        openWidget({ commit }) {
            commit('SET_WIDGET_OPEN', true);
        },

        // Close widget
        closeWidget({ commit }) {
            commit('SET_WIDGET_OPEN', false);
        },

        // Clear chat state (on logout)
        clearState({ commit, dispatch }) {
            dispatch('disconnectSocket');
            commit('CLEAR_STATE');
        }
    },

    getters: {
        isConnected: (state) => state.connected,
        conversations: (state) => state.conversations,
        activeConversation: (state) => state.activeConversation,
        totalUnread: (state) => state.totalUnread,
        isWidgetOpen: (state) => state.widgetOpen,
        isLoading: (state) => state.loading,

        getMessages: (state) => (conversationId) => {
            return state.messages[conversationId] || [];
        },

        getUnreadCount: (state) => (conversationId) => {
            return state.unreadCounts[conversationId] || 0;
        },

        isUserOnline: (state) => (userId) => {
            return state.onlineUsers[userId] || false;
        },

        isTyping: (state) => (conversationId) => {
            const typing = state.typingUsers[conversationId];
            if (!typing) return null;

            const typingUsers = Object.entries(typing)
                .filter(([_, isTyping]) => isTyping)
                .map(([userId]) => userId);

            return typingUsers.length > 0 ? typingUsers : null;
        },

        hasMoreMessages: (state) => (conversationId) => {
            return state.hasMoreMessages[conversationId] || false;
        }
    }
};
